/**
 * Document Scanner Component
 *
 * OCR-powered document scanning interface for smart records capture.
 * Supports image upload, camera capture, and extracted data preview.
 */

import { useState, useCallback, useRef } from 'react';
import { ocrService, OCRResult, ExtractedRecordData } from '../../services/ocr';
import styles from './DocumentScanner.module.css';

interface DocumentScannerProps {
  onScanComplete: (result: OCRResult) => void;
  onExtractedData?: (data: ExtractedRecordData) => void;
  acceptedTypes?: string;
  maxFileSize?: number; // in MB
}

type ScanStep = 'upload' | 'processing' | 'review';

export function DocumentScanner({
  onScanComplete,
  onExtractedData,
  acceptedTypes = 'image/png,image/jpeg,image/jpg,image/webp,application/pdf',
  maxFileSize = 10,
}: DocumentScannerProps) {
  const [step, setStep] = useState<ScanStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<OCRResult | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isCameraMode, setIsCameraMode] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const handleFileSelect = useCallback((selectedFile: File) => {
    // Validate file size
    if (selectedFile.size > maxFileSize * 1024 * 1024) {
      setError(`File size exceeds ${maxFileSize}MB limit`);
      return;
    }

    // Validate file type
    if (!acceptedTypes.split(',').some(type => selectedFile.type.includes(type.replace('*', '')))) {
      setError('Invalid file type. Please upload an image or PDF.');
      return;
    }

    setError(null);
    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };
    reader.readAsDataURL(selectedFile);
  }, [acceptedTypes, maxFileSize]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      setStream(mediaStream);
      setIsCameraMode(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setError('Could not access camera. Please check permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraMode(false);
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const capturedFile = new File([blob], 'captured-document.jpg', { type: 'image/jpeg' });
        handleFileSelect(capturedFile);
        stopCamera();
      }
    }, 'image/jpeg', 0.9);
  }, [handleFileSelect, stopCamera]);

  const processDocument = useCallback(async () => {
    if (!file) return;

    setStep('processing');
    setProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const ocrResult = await ocrService.processImage(file);

      clearInterval(progressInterval);
      setProgress(100);

      setResult(ocrResult);
      setStep('review');
      onScanComplete(ocrResult);

      if (onExtractedData) {
        onExtractedData(ocrResult.extractedData);
      }
    } catch (err) {
      setError('Failed to process document. Please try again.');
      setStep('upload');
    }
  }, [file, onScanComplete, onExtractedData]);

  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setResult(null);
    setProgress(0);
    setError(null);
    stopCamera();
  }, [stopCamera]);

  return (
    <div className={styles.container}>
      {/* Upload Step */}
      {step === 'upload' && !isCameraMode && (
        <div
          className={`${styles.dropzone} ${isDragging ? styles.dragging : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            className={styles.fileInput}
          />

          {preview ? (
            <div className={styles.previewContainer}>
              <img src={preview} alt="Document preview" className={styles.previewImage} />
              <div className={styles.previewOverlay}>
                <button className={styles.changeButton} onClick={(e) => { e.stopPropagation(); reset(); }}>
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.uploadPrompt}>
              <div className={styles.uploadIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <h3>Upload Document</h3>
              <p>Drag and drop or click to browse</p>
              <span className={styles.supportedTypes}>
                Supports: PNG, JPEG, WebP, PDF (max {maxFileSize}MB)
              </span>
            </div>
          )}
        </div>
      )}

      {/* Camera Mode */}
      {step === 'upload' && isCameraMode && (
        <div className={styles.cameraContainer}>
          <video ref={videoRef} autoPlay playsInline className={styles.video} />
          <div className={styles.cameraControls}>
            <button className={styles.captureButton} onClick={capturePhoto}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
              </svg>
            </button>
            <button className={styles.cancelCameraButton} onClick={stopCamera}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Action buttons for upload step */}
      {step === 'upload' && !isCameraMode && (
        <div className={styles.actions}>
          <button className={styles.cameraButton} onClick={startCamera}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Use Camera
          </button>
          {file && (
            <button className={styles.processButton} onClick={processDocument}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Scan Document
            </button>
          )}
        </div>
      )}

      {/* Processing Step */}
      {step === 'processing' && (
        <div className={styles.processing}>
          <div className={styles.processingIcon}>
            <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
          </div>
          <h3>Processing Document</h3>
          <p>Extracting text and identifying records data...</p>
          <div className={styles.progressBar}>
            <div className={styles.progressFill} style={{ width: `${progress}%` }} />
          </div>
          <span className={styles.progressText}>{progress}%</span>
        </div>
      )}

      {/* Review Step */}
      {step === 'review' && result && (
        <div className={styles.review}>
          <div className={styles.reviewHeader}>
            <h3>Scan Results</h3>
            <div className={styles.confidence}>
              <span className={`${styles.confidenceValue} ${result.confidence >= 80 ? styles.high : result.confidence >= 50 ? styles.medium : styles.low}`}>
                {result.confidence.toFixed(0)}%
              </span>
              <span className={styles.confidenceLabel}>Confidence</span>
            </div>
          </div>

          {/* Extracted Data */}
          <div className={styles.extractedData}>
            <h4>Extracted Information</h4>
            <div className={styles.dataGrid}>
              {result.extractedData.staffName && (
                <DataField label="Staff Name" value={result.extractedData.staffName} />
              )}
              {result.extractedData.staffId && (
                <DataField label="Staff ID" value={result.extractedData.staffId} />
              )}
              {result.extractedData.grade && (
                <DataField label="Grade" value={result.extractedData.grade} />
              )}
              {result.extractedData.documentType && result.extractedData.documentType !== 'unknown' && (
                <DataField
                  label="Document Type"
                  value={result.extractedData.documentType.replace(/_/g, ' ')}
                  highlight
                />
              )}
              {result.extractedData.dates.length > 0 && (
                <DataField label="Dates Found" value={result.extractedData.dates.join(', ')} />
              )}
              {result.extractedData.fileNumbers.length > 0 && (
                <DataField label="File Numbers" value={result.extractedData.fileNumbers.join(', ')} />
              )}
              {result.extractedData.signatures > 0 && (
                <DataField label="Signatures" value={`${result.extractedData.signatures} detected`} />
              )}
            </div>
          </div>

          {/* Raw Text */}
          <div className={styles.rawText}>
            <h4>Extracted Text</h4>
            <div className={styles.textContent}>
              {result.text || 'No text could be extracted from this document.'}
            </div>
          </div>

          {/* Actions */}
          <div className={styles.reviewActions}>
            <button className={styles.resetButton} onClick={reset}>
              Scan Another
            </button>
            <button className={styles.useDataButton} onClick={() => onExtractedData?.(result.extractedData)}>
              Use This Data
            </button>
          </div>

          <div className={styles.processingTime}>
            Processed in {(result.processingTime / 1000).toFixed(2)}s
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className={styles.error}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
    </div>
  );
}

function DataField({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`${styles.dataField} ${highlight ? styles.highlight : ''}`}>
      <span className={styles.dataLabel}>{label}</span>
      <span className={styles.dataValue}>{value}</span>
    </div>
  );
}
